import React from 'react';
import { ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: 'Kitchen',
      description: 'Sophisticated kitchen solutions where culinary artistry meets contemporary design. Each custom cabinetry system is designed to maximize functionality while maintaining the warm, elegant aesthetic.',
      image: 'https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/9ptlqe0u_598525352_694759526902158_2178918516253509462_n.jpg',
      align: 'left',
      galleryKey: 'kitchenette'
    },
    {
      title: 'Closets',
      description: 'Organised wardrobe systems that brings functionality, and luxury to your personal space. From walk in closets to reach in storage solutions, we create spaces that adapt seamlessly to your lifestyle with precision and style.',
      image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_54d45426-6e46-45aa-b51c-b65e128b9e72/artifacts/4o6b5w6z_ChatGPT%20Image%20May%2015%2C%202026%2C%2010_48_32%20AM.png',
      align: 'right',
      galleryKey: 'closet'
    },
    {
      title: 'Bathroom Vanities',
      description: 'Luxurious vanity designs that transform your bathroom into a personal sanctuary. Every piece balances form and function, featuring premium materials and finishes that elevate daily rituals.',
      image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_54d45426-6e46-45aa-b51c-b65e128b9e72/artifacts/xnv6ib57_ffeefc43-441b-4b6c-b404-a67f5503064a.png',
      align: 'left',
      galleryKey: 'vanity'
    },
    {
      title: 'Media Units',
      description: 'Custom entertainment centers that seamlessly blend technology with design. Clean lines, integrated storage, and thoughtful cable management create focal points that enhance your living space.',
      image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_54d45426-6e46-45aa-b51c-b65e128b9e72/artifacts/bprs32q6_AD656702-20B5-4F0B-AEA2-9D9622E440B9.png',
      align: 'right',
      galleryKey: 'media'
    }
  ];

  const openGalleryCategory = (galleryKey) => {
    // Scroll to gallery section first
    const galleryElement = document.getElementById('gallery');
    if (galleryElement) {
      galleryElement.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Wait for scroll to complete, then click the category
    setTimeout(() => {
      const categoryCard = document.querySelector(`[data-category-key="${galleryKey}"]`);
      if (categoryCard) {
        categoryCard.click();
      }
    }, 800);
  };

  return (
    <section id="services" className="py-20 sm:py-28 lg:py-32 bg-sand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16 sm:mb-20 lg:mb-24">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-playfair font-light text-navy mb-4 sm:mb-6">
            Our Services
          </h2>
          <div className="w-12 sm:w-16 h-0.5 bg-burgundy mx-auto mb-6 sm:mb-8"></div>
          <p className="text-base sm:text-lg text-warm-charcoal/70 max-w-2xl mx-auto leading-relaxed-xl font-manrope px-4">
            Comprehensive custom furniture solutions, each designed with precision and designed with care for our clients.
          </p>
        </div>

        {/* Alternating service layouts */}
        <div className="space-y-16 sm:space-y-24 lg:space-y-32">
          {services.map((service, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center ${
                service.align === 'right' ? 'lg:grid-flow-dense' : ''
              }`}
            >
              {/* Image */}
              <div className={`${service.align === 'right' ? 'lg:col-start-2' : ''} group`}>
                <div className="relative aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-700"
                  />
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/10 transition-colors duration-500"></div>
                </div>
              </div>

              {/* Content */}
              <div className={`space-y-4 sm:space-y-6 ${service.align === 'right' ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div>
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-light text-navy mb-3 sm:mb-4 leading-tight">
                    {service.title}
                  </h3>
                  <div className="w-10 sm:w-12 h-0.5 bg-gold"></div>
                </div>
                
                <p className="text-base sm:text-lg text-warm-charcoal/80 leading-relaxed-xl font-manrope">
                  {service.description}
                </p>

                <button
                  onClick={() => openGalleryCategory(service.galleryKey)}
                  className="group inline-flex items-center gap-2 text-burgundy hover:text-navy transition-colors duration-300 font-manrope font-medium text-base"
                >
                  <span>View Work</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA at bottom */}
        <div className="text-center mt-32">
          <div className="inline-block bg-warm-white p-12 rounded-2xl shadow-lg">
            <h3 className="text-3xl font-playfair font-light text-navy mb-4">
              Ready to Transform Your Space?
            </h3>
            <p className="text-warm-charcoal/70 font-manrope mb-8 max-w-xl">
              Let's discuss your vision and create something exceptional together.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gold text-navy px-8 py-4 rounded-lg text-base font-manrope font-semibold hover:bg-gold-light transition-all duration-300 shadow-md hover:shadow-lg hover:translate-y-[-2px] inline-flex items-center gap-2"
            >
              <span>Get Custom Quote</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
